import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TaxonomyCardProps {
  label: string
  /** Caminho do webp em /explore/taxonomia. Null cai no bloco de iniciais. */
  imageSrc?: string | null
  selected: boolean
  disabled?: boolean
  onToggle: () => void
  /**
   * `radio` quando a escolha é única (o filtro do Explore), `checkbox` quando é
   * múltipla (o wizard, onde o negócio marca várias subcategorias).
   */
  role?: 'radio' | 'checkbox'
  className?: string
}

/**
 * O card de uma subcategoria do Explore — imagem, nome e a linha de ação.
 *
 * Vem do Figma (nós 80:464 e irmãos) e é o MESMO objeto em dois lugares: o
 * segundo nível do filtro do diretório e o passo 1 do wizard de criação de
 * página. Um componente só de propósito — é a mesma escolha ("de que tipo é
 * esse negócio"), e duas cópias divergiriam no primeiro ajuste de espaçamento.
 *
 * Do desenho vieram as medidas (imagem quadrada com cantos de 16px, o véu preto
 * a 5% por cima dela, rótulo de 14px sobre 12px na ação). As CORES da ação não:
 * o arquivo herdou a paleta de outro produto (laranja #ff4b00), e aqui a marca é
 * azul — então o estado ligado usa o `primary` da aplicação, o mesmo do resto
 * dos filtros.
 *
 * O CLIQUE responde em três tempos, e não só na borda: o card afunda enquanto o
 * dedo está nele (`active:scale`), sobe um degrau quando fica ligado, e o selo
 * de check entra por cima da foto. Só a borda mudando — que era o estado
 * anterior — não dava resposta nenhuma numa fileira de trinta e dois cards: a
 * pessoa clicava e não tinha certeza de que tinha marcado. Tudo passa por
 * `motion-reduce`, como o zoom da imagem já fazia.
 */
export default function TaxonomyCard({
  label,
  imageSrc,
  selected,
  disabled = false,
  onToggle,
  role = 'checkbox',
  className,
}: TaxonomyCardProps) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={selected}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        'group flex w-[136px] shrink-0 flex-col items-center gap-3 rounded-2xl border bg-white px-2 pb-4 pt-2 text-center shadow-[0px_1px_1px_rgba(16,24,40,0.05)]',
        'transition-all duration-200 ease-out motion-reduce:transition-none',
        selected
          ? 'border-primary bg-primary/5 shadow-[0px_4px_16px_rgba(4,98,224,0.18)] ring-2 ring-primary'
          : 'border-transparent hover:border-gray-200 hover:shadow-[0px_4px_12px_rgba(16,24,40,0.08)]',
        // O afundar do toque só vale para quem pode clicar: num card travado
        // ele viraria resposta a um clique que não acontece.
        !disabled && 'active:scale-[0.97] motion-reduce:active:scale-100',
        disabled &&
          'cursor-not-allowed opacity-40 hover:border-transparent hover:shadow-none',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        className,
      )}
    >
      {/* `overflow-hidden` no invólucro é o que recorta o zoom: sem ele a
          imagem cresce para fora do card e empurra a fileira. */}
      <span className="relative block size-[100px] shrink-0 overflow-hidden rounded-2xl bg-gray-100">
        {imageSrc && (
          <img
            src={imageSrc}
            alt=""
            loading="lazy"
            // Zoom no hover, e só no hover de ponteiro fino: no toque não há
            // hover, e `motion-reduce` respeita quem pediu menos animação.
            className="size-full object-cover transition-transform duration-300 ease-out motion-reduce:transition-none group-hover:scale-110"
          />
        )}
        {/* O véu de 5% do desenho: segura o contraste do texto branco que algumas
            fotos têm e uniformiza fotos de exposições muito diferentes. Quando
            o card está ligado ele vira azul, para a foto inteira acusar a
            escolha — não só a moldura. */}
        <span
          aria-hidden="true"
          className={cn(
            'absolute inset-0 rounded-2xl transition-colors duration-200 motion-reduce:transition-none',
            selected ? 'bg-primary/20' : 'bg-black/5',
          )}
        />

        {/* O selo do escolhido. Sempre montado (só escondido) para poder ANIMAR
            a entrada: montar no clique faria ele aparecer pronto, sem transição,
            que é a "dureza" que este card tinha. */}
        <span
          aria-hidden="true"
          className={cn(
            'absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm',
            'transition-all duration-200 ease-out motion-reduce:transition-none',
            selected ? 'scale-100 opacity-100' : 'scale-50 opacity-0',
          )}
        >
          <Check className="size-4" strokeWidth={3} />
        </span>
      </span>

      <span className="flex w-full flex-col items-center gap-2">
        <span className="w-full break-words text-sm font-semibold leading-tight text-gray-800">
          {label}
        </span>
        <span
          className={cn(
            'text-xs font-bold transition-colors duration-200 motion-reduce:transition-none',
            selected ? 'text-primary' : 'text-gray-400',
          )}
        >
          {selected ? 'Selecionado' : 'Selecionar'}
        </span>
      </span>
    </button>
  )
}
